// NOTICE: KAPTION-INTEGRATION
// security-center.UI.4 — shared wording for the Security Center: provider and
// domain names, dates, and one sentence per security event.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const KNOWN_EVENTS = [
  'sso_sign_in',
  'sso_sign_in_denied',
  'password_sign_in',
  'password_sign_in_failed',
  'password_sign_in_blocked',
  'sso_connection_saved',
  'sso_connection_removed',
  'sso_connection_enabled',
  'sso_test',
  'sign_in_policy_changed',
  'provisioning_changed',
  'user_provisioned',
  'role_changed_by_sso',
  'admin_role_kept',
  'domain_added',
  'domain_verified',
  'domain_removed',
  'domain_record_missing',
  'two_step_policy_changed',
  'two_step_reset',
  'emergency_access_used',
  'session_policy_changed',
  'sessions_revoked',
  'session_reauth_required',
];
const KNOWN_REASONS = [
  'SSO-DOMINIO',
  'SSO-SIN-GRUPO',
  'SSO-PROTEGIDO',
  'SSO-EMAIL',
  'SSO-SIN-CUENTA',
  'SSO-INACTIVO',
  'SSO-LIMITE',
  'SSO-ESTADO',
  'SSO-PROVEEDOR',
  'SSO-NO-DISPONIBLE',
];

export function useKaptionSecurityText(overviewRef) {
  const { t, locale } = useI18n();
  const intlLocale = computed(() =>
    String(locale.value || 'en').replace('_', '-')
  );

  const provider = computed(
    () =>
      overviewRef.value?.connection?.label ||
      t('KAPTION_SECURITY.COMMON.YOUR_PROVIDER')
  );
  const verified = computed(() =>
    (overviewRef.value?.domains || [])
      .filter(d => d.status === 'verified')
      .map(d => d.domain)
  );
  const domainsText = computed(() =>
    verified.value.length
      ? new Intl.ListFormat(intlLocale.value, { type: 'conjunction' }).format(
          verified.value
        )
      : t('KAPTION_SECURITY.COMMON.YOUR_DOMAINS')
  );

  const formatWhen = iso => {
    if (!iso) return '';
    const date = new Date(iso);
    const now = new Date();
    const sameDay = date.toDateString() === now.toDateString();
    const opts = sameDay
      ? { hour: '2-digit', minute: '2-digit' }
      : { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' };
    return new Intl.DateTimeFormat(intlLocale.value, opts).format(date);
  };

  const formatRelative = iso => {
    if (!iso) return '';
    const seconds = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
    const rtf = new Intl.RelativeTimeFormat(intlLocale.value, {
      numeric: 'auto',
    });
    const abs = Math.abs(seconds);
    if (abs < 60) return rtf.format(seconds, 'second');
    if (abs < 3600) return rtf.format(Math.round(seconds / 60), 'minute');
    if (abs < 86400) return rtf.format(Math.round(seconds / 3600), 'hour');
    return rtf.format(Math.round(seconds / 86400), 'day');
  };

  const roleName = role =>
    role === 'administrator'
      ? t('KAPTION_SECURITY.USERS.ADMINISTRATOR')
      : t('KAPTION_SECURITY.USERS.AGENT');

  const reasonText = code => {
    const account = overviewRef.value?.account_name || '';
    if (KNOWN_REASONS.includes(code))
      return t(`KAPTION_SECURITY.LOGIN.REASONS.${code}`, {
        provider: provider.value,
        account,
      });
    return (
      code ||
      t('KAPTION_SECURITY.LOGIN.REASONS.generic', { provider: provider.value })
    );
  };

  const policyName = value =>
    ['password', 'sso_available', 'sso_required'].includes(value)
      ? t(`KAPTION_SECURITY.ACTIVITY.POLICIES.${value}`)
      : value;

  const eventText = event => {
    const details = event.details || {};
    const subject =
      event.subject?.name ||
      event.subject?.email ||
      event.email ||
      t('KAPTION_SECURITY.ACTIVITY.SOMEONE');
    const params = {
      subject,
      email: event.email || subject,
      provider: provider.value,
      domain: details.domain || '',
      reason: event.reason_code || '',
      role: details.role ? roleName(details.role) : '',
      from: details.from ? roleName(details.from) : '',
      to: details.to ? roleName(details.to) : '',
      result:
        event.outcome === 'success'
          ? t('KAPTION_SECURITY.ACTIVITY.RESULT_OK')
          : t('KAPTION_SECURITY.ACTIVITY.RESULT_FAILED'),
      policy: policyName(details.sign_in_policy?.[1] || ''),
      type: event.event_type,
    };
    if (event.event_type === 'sso_sign_in' && details.jit)
      return t('KAPTION_SECURITY.ACTIVITY.EVENTS.sso_sign_in_jit', params);
    if (KNOWN_EVENTS.includes(event.event_type))
      return t(`KAPTION_SECURITY.ACTIVITY.EVENTS.${event.event_type}`, params);
    return t('KAPTION_SECURITY.ACTIVITY.EVENTS.other', params);
  };

  const eventWho = event =>
    event.actor?.name ||
    event.actor?.email ||
    t('KAPTION_SECURITY.ACTIVITY.SYSTEM');

  return {
    provider,
    domainsText,
    verified,
    formatWhen,
    formatRelative,
    roleName,
    reasonText,
    eventText,
    eventWho,
  };
}
